<script setup lang="ts">
import Typography from '@components/Typography.vue';
import { formatNumber } from '@tools/number';
import { minBy } from 'lodash-es';
import Slider from 'primevue/slider';
import {
  computed,
  ref,
  watch,
  type ComponentPublicInstance,
} from 'vue';

export interface RulerMark {
  index: number,
  label: string | number,
  align: 'left' | 'center' | 'right',
}

interface RangeSliderProps {
  modelValue: [ number, number ],
  values: number[],
  majorTicks?: number[],
  marks?: RulerMark[],
  isActive?: boolean,
}

const {
  modelValue,
  values,
  majorTicks = [],
  marks = [],
  isActive = false,
} = defineProps<RangeSliderProps>();

const emit = defineEmits<{(e: 'update:modelValue', value: [ number, number ]): void;}>();

type SliderInstance = ComponentPublicInstance & {
  d_value: [number, number],
  dragging: boolean,
};

const sliderRef = ref<SliderInstance | null>(null);

const sliderMinIndex = 0;
const sliderMaxIndex = computed<number>(() => Math.max(0, values.length - 1));

const getPercentage = (index: number): number => {
  if (sliderMaxIndex.value === 0) {
    return 0;
  }

  return (index / sliderMaxIndex.value) * 100;
};

const isMajorTick = (value: number, index: number): boolean => {
  if (majorTicks) {
    return majorTicks.includes(value) || index === sliderMinIndex || index === sliderMaxIndex.value;
  }

  return index === sliderMinIndex || index === sliderMaxIndex.value;
};

const getMarkAlignmentClass = (align: 'left' | 'center' | 'right'): string => {
  if (align === 'left') {
    return '-left-1';
  }

  if (align === 'right') {
    return '-right-5 text-right';
  }

  return '-translate-x-1/2 text-center';
};

const getClosestIndex = (
  value: number | null | undefined,
  defaultIndex: number,
): number => {
  if (value == null) {
    return defaultIndex;
  }

  const closestValue = minBy(
    values,
    (sliderValue) => Math.abs(sliderValue - value),
  );

  return closestValue === undefined
    ? defaultIndex
    : values.indexOf(closestValue);
};

const rulerMarks = computed<RulerMark[]>(() => {
  if (marks.length) {
    return marks;
  }

  if (majorTicks.length) {
    return majorTicks.map((tickValue, tickIndex, allTicks) => {
      const sliderIndex = getClosestIndex(tickValue, 0);
      const isFirstTick = tickIndex === 0;
      const isLastTick = tickIndex === allTicks.length - 1;

      if (isFirstTick) {
        return {
          index: sliderIndex,
          label: tickValue,
          align: 'left',
        };
      }

      if (isLastTick) {
        return {
          index: sliderIndex,
          label: `${formatNumber(tickValue)} +`,
          align: 'right',
        };
      }

      return {
        index: sliderIndex,
        label: formatNumber(tickValue),
        align: 'center',
      };
    });
  }

  const firstValue = values[sliderMinIndex] ?? 0;
  const lastValue = values[sliderMaxIndex.value] ?? 0;

  return [
    {
      index: sliderMinIndex,
      label: firstValue,
      align: 'left',
    },
    {
      index: sliderMaxIndex.value,
      label: `${formatNumber(lastValue)} +`,
      align: 'right',
    },
  ];
});

const clampRange = (value: [ number, number ]): [ number, number ] => {
  let [ minIndex, maxIndex ] = value;
  const [ currentMin, currentMax ] = modelValue;

  if (minIndex > maxIndex) {
    if (minIndex !== currentMin) {
      minIndex = currentMax;
    } else {
      maxIndex = currentMin;
    }
  }

  return [ minIndex, maxIndex ];
};

const isRecentlySlid = ref<boolean>(false);
let slideEndTimeout: ReturnType<typeof setTimeout> | null = null;

const onSliderUpdate = (value: number | number[]): void => {
  if (!Array.isArray(value) || value.length < 2) {
    return;
  }

  const [ minIndex, maxIndex ] = clampRange([ value[0], value[1] ]);

  if (sliderRef.value) {
    sliderRef.value.d_value = [ minIndex, maxIndex ];
  }

  emit('update:modelValue', [ minIndex, maxIndex ]);
};

const onSlideEnd = (): void => {
  if (sliderRef.value) {
    const [ currentMin, currentMax ] = modelValue;
    sliderRef.value.d_value = [ currentMin, currentMax ];
  }

  isRecentlySlid.value = true;
  if (slideEndTimeout) {
    clearTimeout(slideEndTimeout);
  }

  slideEndTimeout = setTimeout(() => {
    isRecentlySlid.value = false;
  }, 100);
};

watch(
  () => modelValue,
  (newValue) => {
    if (sliderRef.value && newValue) {
      sliderRef.value.d_value = [ newValue[0], newValue[1] ];
    }
  },
  { deep: true },
);

const setSliderClosestValue = (targetIndex: number): void => {
  const [ currentMin, currentMax ] = modelValue;

  if (targetIndex <= currentMin) {
    emit('update:modelValue', [ targetIndex, currentMax ]);
    return;
  }

  if (targetIndex >= currentMax) {
    emit('update:modelValue', [ currentMin, targetIndex ]);
    return;
  }

  const distMin = targetIndex - currentMin;
  const distMax = currentMax - targetIndex;

  if (distMin <= distMax) {
    emit('update:modelValue', [ targetIndex, currentMax ]);
  } else {
    emit('update:modelValue', [ currentMin, targetIndex ]);
  }
};

const getNewRangeFromClick = (
  clickPosition: number,
  rect: DOMRect,
): [ number, number ] => {
  const percentagePosition = Math.max(
    0,
    Math.min(1, (clickPosition - rect.left) / rect.width),
  );

  const targetIndex = Math.round(
    sliderMinIndex
    + percentagePosition * (sliderMaxIndex.value - sliderMinIndex),
  );

  const [ currentMin, currentMax ] = modelValue;

  if (targetIndex <= currentMin) {
    return [ targetIndex, currentMax ];
  }

  if (targetIndex >= currentMax) {
    return [ currentMin, targetIndex ];
  }

  const distMin = targetIndex - currentMin;
  const distMax = currentMax - targetIndex;

  if (distMin <= distMax) {
    return [ targetIndex, currentMax ];
  }

  return [ currentMin, targetIndex ];
};

const handleSliderClick = (event: MouseEvent): void => {
  if (isRecentlySlid.value || sliderRef.value?.dragging) {
    event.stopPropagation();
    event.preventDefault();
    return;
  }

  const target = event.target as HTMLElement | null;

  if (!target) {
    return;
  }

  if (
    target.getAttribute('role') === 'slider'
    || target.getAttribute('data-pc-section') === 'handle'
    || target.getAttribute('data-pc-section') === 'startHandler'
    || target.getAttribute('data-pc-section') === 'endHandler'
  ) {
    return;
  }

  const sliderEl = sliderRef.value?.$el as HTMLElement | undefined;

  if (!sliderEl) {
    return;
  }

  const rect = sliderEl.getBoundingClientRect();

  if (rect.width === 0) {
    return;
  }

  event.stopPropagation();

  emit('update:modelValue', getNewRangeFromClick(event.clientX, rect));
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      class="cursor-pointer py-2.5"
      @click.capture="handleSliderClick"
    >
      <Slider
        ref="sliderRef"
        :model-value="modelValue"
        range
        :min="sliderMinIndex"
        :max="sliderMaxIndex"
        :step="1"
        class="max-w-3xs mx-auto w-full"
        :pt:range:class="[
          'h-full',
          isActive ? 'bg-blue' : 'bg-gray-dark',
        ]"
        @update:model-value="onSliderUpdate"
        @slideend="onSlideEnd"
      />
    </div>

    <div class="relative w-full select-none">
      <div class="relative h-2 w-full">
        <div
          v-for="(sliderValue, index) in values"
          :key="sliderValue"
          :class="[
            'absolute top-0 -translate-x-1/2 rounded-full',
            isMajorTick(sliderValue, index)
              ? 'h-2 w-[1.5px] bg-gray-dark'
              : 'h-1 w-px bg-gray-light'
          ]"
          :style="{ left: `${getPercentage(index)}%` }"
        />
      </div>

      <div class="relative mt-1 h-5 w-full">
        <span
          v-for="mark in rulerMarks"
          :key="mark.label"
          :class="[
            'absolute cursor-pointer transition-colors hover:text-blue',
            getMarkAlignmentClass(mark.align)
          ]"
          :style="mark.align === 'center' ? { left: `${getPercentage(mark.index)}%` } : {}"
          @click="setSliderClosestValue(mark.index)"
        >
          <Typography
            variant="caption"
            color="gray-dark"
          >
            {{ mark.label }}
          </Typography>
        </span>
      </div>
    </div>
  </div>
</template>
